const SEG_BASE = "h-2.5 flex-1 rounded-full transition-colors";
const SEG_STYLES = {
  done: "bg-indigo-600",
  now: "bg-indigo-600 outline outline-2 outline-offset-2 outline-indigo-50",
  up: "bg-slate-200",
};

const LABEL_BASE = "flex-1 text-center text-[12.5px] leading-tight";
const LABEL_STYLES = {
  done: "font-medium text-slate-700",
  now: "font-bold text-indigo-600",
  up: "font-medium text-slate-400",
};

/**
 * Segmented progress bar for a pilot's stage. Purely presentational.
 *
 * Stages come from the team's HubSpot pilot config (BE `pilot.pilot_deal_stages`),
 * so the count + labels vary per team. Stages before the current one render as
 * completed, the matching one as current, the rest as upcoming.
 *
 * @param {Array<{id: string, name: string}>} stages - Ordered pilot stages.
 *   Renders nothing when empty (team has no pilot stage config).
 * @param {string} currentStageId - HubSpot stage id of the account's current
 *   pilot stage. When null/unmatched, every stage renders as upcoming (no
 *   "current") rather than guessing — avoids confidently mislabeling.
 */
export const PilotStageBar = ({ stages = [], currentStageId }) => {
  if (!stages.length) return null;

  const activeIndex = stages.findIndex((s) => s.id === currentStageId);

  const stateAt = (i) =>
    i < activeIndex ? "done" : i === activeIndex ? "now" : "up";

  return (
    <div className="flex flex-col gap-3" data-testid="pilot-stage-bar">
      <div className="flex gap-2">
        {stages.map((stage, i) => (
          <div
            key={stage.id}
            className={`${SEG_BASE} ${SEG_STYLES[stateAt(i)]}`}
            aria-hidden="true"
          />
        ))}
      </div>
      <div className="flex gap-2">
        {stages.map((stage, i) => {
          const state = stateAt(i);
          return (
            <span
              key={stage.id}
              className={`${LABEL_BASE} ${LABEL_STYLES[state]}`}
              aria-current={state === "now" ? "step" : undefined}
            >
              {stage.name}
            </span>
          );
        })}
      </div>
    </div>
  );
};
